import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { useToast } from '../../components/Toast';
import useUrlState from '../../hooks/useUrlState';
import extractApiError from '../../utils/apiError';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { logError } from '../../utils/logError';
import { formatTutarTR } from '../../utils/para';

// PR-B (PB3) + PE3 — Satış İrsaliyeleri listesi.
// /api/satis-irsaliyeleri (GET) + onayla/iptal aksiyonları. Müşteri adı için
// /api/cari?tip=MUSTERI eşlemesi. Detay satır içinde açılır (GET /{id} → kalemler,
// SatisFaturalariPage deseni). PDF (Fiyatlı/Fiyatsız) blob fetch ile token'lı indirilir.
// Anayasa M.5: onayla/iptal MANAGE_SATIS ile, "+ Yeni İrsaliye" + create formu
// CREATE_SATIS_IRSALIYE (depo dahil) ile gizlenir (backend zaten enforce eder).
// PDF okuma-only → yetki kapısı yok (depo basar). Force onay yalnız yonetim/admin.

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Durum → rozet rengi (TASLAK gri, SEVK_EDILDI mavi, FATURALANDI yeşil, IPTAL kırmızı)
const DURUM_BADGE = {
  TASLAK: { bg: '#e2e3e5', fg: '#383d41', label: 'Taslak' },
  SEVK_EDILDI: { bg: '#cce5ff', fg: '#004085', label: 'Sevk Edildi' },
  FATURALANDI: { bg: '#d4edda', fg: '#155724', label: 'Faturalandı' },
  IPTAL: { bg: '#f8d7da', fg: '#721c24', label: 'İptal' },
};

const DURUM_FILTRE = [
  { value: '', label: 'Tümü' },
  { value: 'TASLAK', label: 'Taslak' },
  { value: 'SEVK_EDILDI', label: 'Sevk Edildi' },
  { value: 'FATURALANDI', label: 'Faturalandı' },
  { value: 'IPTAL', label: 'İptal' },
];

const fmtTarih = (val) => {
  if (!val) return '-';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  return d.toLocaleDateString('tr-TR');
};

const DurumBadge = ({ durum }) => {
  const b = DURUM_BADGE[durum] || { bg: colors.backgroundAlt, fg: colors.textSecondary, label: durum || '-' };
  return (
    <span style={{
      padding: '4px 10px', borderRadius: radius.lg, fontSize: '12px', fontWeight: 'bold',
      background: b.bg, color: b.fg, whiteSpace: 'nowrap',
    }}>
      {b.label}
    </span>
  );
};

const SatisIrsaliyeleriPage = () => {
  const { user } = useAuth();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_SATIS, user.effectivePermissions);
  // "+ Yeni İrsaliye" + create formu: depo CREATE_SATIS_IRSALIYE ile oluşturur (onaylamaz).
  const canCreate = (user && hasPermission(user.role, PERMISSIONS.CREATE_SATIS_IRSALIYE, user.effectivePermissions)) || canManage;
  const canForce = user && (user.role === 'yonetim' || user.role === 'admin');
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [cariMap, setCariMap] = useState({}); // cari_id → unvan
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null); // aksiyon süren satır
  const [detayId, setDetayId] = useState(null); // açık detay satırı
  const [detay, setDetay] = useState(null); // GET /{id} sonucu (kalemler)
  const [detayLoading, setDetayLoading] = useState(false);
  const [pdfBusyId, setPdfBusyId] = useState(null); // PDF indiren satır

  const [durum, setDurum] = useUrlState('irsal_durum', '');
  const [arama, setArama] = useUrlState('irsal_q', '', { debounceMs: 300 });

  const token = localStorage.getItem('access_token');

  // Müşteri eşlemesi (cari_id → unvan). Bir kez yüklenir.
  useEffect(() => {
    fetch(`${API}/api/cari?tip=MUSTERI&include_inactive=true`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const map = {};
        (Array.isArray(data) ? data : []).forEach((c) => { map[c.id] = c.unvan; });
        setCariMap(map);
      })
      .catch(() => {});
  }, [token]);

  const fetchRows = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (durum) params.set('durum', durum);
      const res = await fetch(`${API}/api/satis-irsaliyeleri?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(extractApiError(data.detail));
        return;
      }
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      logError('Satış irsaliyesi yükleme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  }, [token, durum, toast]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const cariAd = (id) => cariMap[id] || `#${id}`;

  const handleDetay = async (ir) => {
    // Toggle: aynı satıra tekrar tıklanırsa kapat
    if (detayId === ir.id) {
      setDetayId(null);
      setDetay(null);
      return;
    }
    setDetayId(ir.id);
    setDetay(null);
    setDetayLoading(true);
    try {
      const res = await fetch(`${API}/api/satis-irsaliyeleri/${ir.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setDetay(data);
      } else {
        toast.error(extractApiError(data.detail));
        setDetayId(null);
      }
    } catch (err) {
      logError('İrsaliye detay hatası:', err);
      toast.error('Bağlantı hatası');
      setDetayId(null);
    } finally {
      setDetayLoading(false);
    }
  };

  // PDF blob fetch (token'lı): window.open URL'i Authorization header taşımaz → fetch + blob.
  // fiyat=true → birim fiyat/KDV/tutar; fiyat=false → fiyatsız sevk irsaliyesi.
  const indirPdf = async (ir, fiyat) => {
    setPdfBusyId(ir.id);
    try {
      const res = await fetch(`${API}/api/satis-irsaliyeleri/${ir.id}/pdf?fiyat=${fiyat}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(extractApiError(d.detail));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank'); // PDF'i yeni sekmede aç (inline)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      logError('İrsaliye PDF hatası:', err);
      toast.error('PDF alınamadı');
    } finally {
      setPdfBusyId(null);
    }
  };

  const handleOnayla = async (ir, force = false) => {
    if (!window.confirm(
      force
        ? `"${ir.irsaliye_no}" irsaliyesini stok yetersiz olsa da (negatif stok) onaylamak istediğinize emin misiniz?`
        : `"${ir.irsaliye_no}" irsaliyesini onaylamak istediğinize emin misiniz?\n\nStok üretim lokasyonundan düşülür (sevk edilir).`,
    )) return;
    setBusyId(ir.id);
    try {
      const url = `${API}/api/satis-irsaliyeleri/${ir.id}/onayla${force ? '?force=true' : ''}`;
      const res = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`"${ir.irsaliye_no}" onaylandı (sevk edildi)`);
        if (detayId === ir.id) { setDetayId(null); setDetay(null); }
        fetchRows();
      } else {
        // Yetersiz stok → 400 + açıklayıcı mesaj. Force varsa kullanıcı ikinci buton ile dener.
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('İrsaliye onaylama hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setBusyId(null);
    }
  };

  const handleIptal = async (ir) => {
    if (!window.confirm(
      `"${ir.irsaliye_no}" irsaliyesini iptal etmek istediğinize emin misiniz?\n\nSevk edilmişse stok geri alınır (storno).`,
    )) return;
    setBusyId(ir.id);
    try {
      const res = await fetch(`${API}/api/satis-irsaliyeleri/${ir.id}/iptal`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`"${ir.irsaliye_no}" iptal edildi`);
        if (detayId === ir.id) { setDetayId(null); setDetay(null); }
        fetchRows();
      } else {
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('İrsaliye iptal hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setBusyId(null);
    }
  };

  // İstemci-taraf arama: irsaliye_no veya müşteri adı (liste ucu zaten 500 ile sınırlı)
  const gorunenRows = rows.filter((r) => {
    if (!arama) return true;
    const q = arama.toLocaleLowerCase('tr-TR');
    const no = (r.irsaliye_no || '').toLocaleLowerCase('tr-TR');
    const ad = (cariMap[r.cari_id] || '').toLocaleLowerCase('tr-TR');
    return no.includes(q) || ad.includes(q);
  });

  if (loading && rows.length === 0) {
    return (
      <div>
        <Header title="Satış İrsaliyeleri" />
        <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>Yükleniyor…</div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Satış İrsaliyeleri" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Üst bar: başlık + yeni butonu */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: colors.textPrimary }}>🧾 Satış İrsaliyeleri</h2>
        </div>

        {/* Filtreler: durum + arama */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
          <select
            value={durum}
            onChange={(e) => setDurum(e.target.value)}
            style={{ padding: '10px 14px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '14px', fontWeight: 'bold' }}
          >
            {DURUM_FILTRE.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="İrsaliye no veya müşteri ara…"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
            style={{ flex: 1, minWidth: '250px', padding: '10px 14px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '14px' }}
          />
        </div>

        {/* Tablo */}
        <div style={{ background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>İrsaliye No</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Tarih</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Müşteri</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>Durum</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', width: '360px' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {gorunenRows.map((ir) => (
                  <React.Fragment key={ir.id}>
                  <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '13px', color: '#1565c0' }}>{ir.irsaliye_no || '-'}</td>
                    <td style={{ padding: '10px 12px' }}>{fmtTarih(ir.tarih)}</td>
                    <td style={{ padding: '10px 12px' }}><strong>{cariAd(ir.cari_id)}</strong></td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}><DurumBadge durum={ir.durum} /></td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleDetay(ir)}
                          style={{ padding: '6px 12px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: detayId === ir.id ? colors.backgroundAlt : 'white', color: colors.textPrimary, cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                        >
                          {detayId === ir.id ? 'Gizle' : 'Detay'}
                        </button>
                        <button
                          onClick={() => indirPdf(ir, true)}
                          disabled={pdfBusyId === ir.id}
                          title="Fiyatlı PDF (birim fiyat + KDV + tutar)"
                          style={{ padding: '6px 12px', borderRadius: radius.sm, border: '1px solid #1565c0', background: 'white', color: '#1565c0', cursor: pdfBusyId === ir.id ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                        >
                          PDF (Fiyatlı)
                        </button>
                        <button
                          onClick={() => indirPdf(ir, false)}
                          disabled={pdfBusyId === ir.id}
                          title="Fiyatsız sevk irsaliyesi"
                          style={{ padding: '6px 12px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: 'white', color: colors.textSecondary, cursor: pdfBusyId === ir.id ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                        >
                          PDF (Fiyatsız)
                        </button>
                        {canCreate && ir.durum === 'TASLAK' && (
                          <Link
                            to={`/satislar/irsaliye/yeni?duzenle=${ir.id}`}
                            title="Taslak irsaliyeyi düzenle"
                            style={{
                              padding: '6px 12px',
                              borderRadius: radius.sm,
                              border: '1px solid #6f42c1',
                              background: 'white',
                              color: '#6f42c1',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                            }}
                          >
                            Düzenle
                          </Link>
                        )}
                        {canManage && ir.durum === 'TASLAK' && (
                          <button
                            onClick={() => handleOnayla(ir, false)}
                            disabled={busyId === ir.id}
                            style={{ padding: '6px 12px', borderRadius: radius.sm, border: 'none', background: '#198754', color: 'white', cursor: busyId === ir.id ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                          >
                            Onayla
                          </button>
                        )}
                        {canManage && canForce && ir.durum === 'TASLAK' && (
                          <button
                            onClick={() => handleOnayla(ir, true)}
                            disabled={busyId === ir.id}
                            title="Stok yetersiz olsa da onayla (negatif stoğa izin)"
                            style={{ padding: '6px 12px', borderRadius: radius.sm, border: '1px solid #fd7e14', background: 'white', color: '#fd7e14', cursor: busyId === ir.id ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                          >
                            Yine de Onayla
                          </button>
                        )}
                        {canManage && (ir.durum === 'TASLAK' || ir.durum === 'SEVK_EDILDI') && (
                          <button
                            onClick={() => handleIptal(ir)}
                            disabled={busyId === ir.id}
                            style={{ padding: '6px 12px', borderRadius: radius.sm, border: 'none', background: '#d32f2f', color: 'white', cursor: busyId === ir.id ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                          >
                            İptal
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {detayId === ir.id && (
                    <tr style={{ borderBottom: `1px solid ${colors.border}`, background: colors.backgroundAlt }}>
                      <td colSpan={5} style={{ padding: '12px 16px' }}>
                        {detayLoading && <div style={{ color: colors.textSecondary, fontSize: '13px' }}>Yükleniyor…</div>}
                        {!detayLoading && detay && (
                          <div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                              <thead>
                                <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Malzeme</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Miktar</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Birim</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Birim Fiyat</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>KDV %</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Tutar</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(detay.kalemler || []).map((k) => (
                                  <tr key={k.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '6px 8px' }}>{k.malzeme_ad || `#${k.malzeme_id}`}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTutarTR(k.miktar)}</td>
                                    <td style={{ padding: '6px 8px' }}>{k.birim || '-'}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTutarTR(k.birim_fiyat)}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{k.kdv_orani}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>{formatTutarTR(k.satir_brut)}</td>
                                  </tr>
                                ))}
                                {(detay.kalemler || []).length === 0 && (
                                  <tr><td colSpan={6} style={{ padding: '10px', color: colors.textTertiary }}>Kalem yok.</td></tr>
                                )}
                              </tbody>
                            </table>
                            {(() => {
                              const kalemler = detay.kalemler || [];
                              const net = kalemler.reduce((s, k) => s + (k.satir_net || 0), 0);
                              const kdv = kalemler.reduce((s, k) => s + (k.satir_kdv || 0), 0);
                              const brut = kalemler.reduce((s, k) => s + (k.satir_brut || 0), 0);
                              return (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', marginTop: '8px', fontSize: '13px' }}>
                                  <span style={{ color: colors.textSecondary }}>Net: <strong style={{ fontFamily: 'monospace' }}>{formatTutarTR(net)} TL</strong></span>
                                  <span style={{ color: colors.textSecondary }}>KDV: <strong style={{ fontFamily: 'monospace' }}>{formatTutarTR(kdv)} TL</strong></span>
                                  <span style={{ color: colors.textPrimary }}>Genel: <strong style={{ fontFamily: 'monospace', color: styles.primary }}>{formatTutarTR(brut)} TL</strong></span>
                                </div>
                              );
                            })()}
                            {detay.notlar && (
                              <div style={{ marginTop: '6px', fontSize: '12px', color: colors.textSecondary }}>Not: {detay.notlar}</div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}

                {gorunenRows.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
                      {arama || durum ? 'Kritere uygun irsaliye bulunamadı.' : 'Henüz satış irsaliyesi oluşturulmamış.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SatisIrsaliyeleriPage;
